<script setup lang="ts">
/**
 * Onboarding tour — step 2 body (M4C-15).
 *
 * "Your activity becomes a sky": a decorative constellation illustration above
 * the heading and body copy. One highlighted constellation (gold dots + solid
 * lines) sits among faint, dotted constellations. The illustration is a hand-
 * authored, hex-free inline SVG — colored through the `star` and `fontColor`
 * tokens so it carries no expiring Figma asset URLs. The chrome (progress
 * header, footer nav, border, backdrop) is owned by the tour shell (M4C-13);
 * this component renders body content only.
 */
const { t } = useI18n();
</script>

<template>
  <div class="flex flex-col gap-16">
    <!-- Constellation illustration: one active (gold) constellation among faint ones -->
    <div class="h-[176px] w-full">
      <svg
        viewBox="0 0 310 176"
        fill="none"
        aria-hidden="true"
        preserveAspectRatio="xMinYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        class="block h-full w-auto"
      >
        <!-- Faint constellations: dashed lines + hollow dots -->
        <g
          class="stroke-fontColor-w80"
          fill="none"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-dasharray="3 3"
        >
          <path d="M78.5 168 L116.5 106 L168.5 85" />
          <path d="M116.5 106 L149.5 123 L205.5 135" />
          <path d="M217.5 64 L243.7 38 L299.7 54 L276.7 87 L284.7 100" />
          <path d="M241.7 4 L285.7 9 L299.7 54" />
        </g>
        <g class="fill-white stroke-fontColor-w80" stroke-width="1.5">
          <circle cx="116.5" cy="106" r="3.25" />
          <circle cx="149.5" cy="123" r="3.25" />
          <circle cx="168.5" cy="85" r="3.25" />
          <circle cx="205.5" cy="135" r="3.25" />
          <circle cx="217.5" cy="64" r="3.25" />
          <circle cx="243.7" cy="38" r="3.25" />
          <circle cx="241.7" cy="4" r="3.25" />
          <circle cx="299.7" cy="54" r="3.25" />
          <circle cx="276.7" cy="87" r="3.25" />
          <circle cx="284.7" cy="100" r="3.25" />
          <circle cx="285.7" cy="9" r="3.25" />
        </g>

        <!-- Active constellation: solid gold lines + filled dots -->
        <g
          class="stroke-star"
          fill="none"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 4 L99 17 L127 31 L132.5 48" />
          <path d="M49.5 144 L78.5 172" />
        </g>
        <g class="fill-star">
          <circle cx="4" cy="4" r="4" />
          <circle cx="99" cy="17" r="4" />
          <circle cx="127" cy="31" r="4" />
          <circle cx="132.5" cy="48" r="4" />
          <circle cx="49.5" cy="144" r="4" />
          <circle cx="78.5" cy="172" r="4" />
        </g>
      </svg>
    </div>

    <!-- Heading + body copy (40px gap) -->
    <div class="flex flex-col gap-10">
      <p class="text-h1">{{ t('onboarding.tour.step2.heading') }}</p>
      <p class="text-body">{{ t('onboarding.tour.step2.body') }}</p>
    </div>
  </div>
</template>
